import { useCallback, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  DefaultErrorFallback,
  ErrorBoundary,
} from "@norkron/web-error-boundary";
import { RequireAuth } from "./components/RouteGuard";
import { AppShell } from "./shell/AppShell";
import { Activity } from "./screens/Activity";
import { Admin } from "./screens/Admin";
import { Articles } from "./screens/Articles";
import { ArticleWorkbench } from "./screens/ArticleWorkbench";
import { DocumentViewer } from "./screens/DocumentViewer";
import { Governance } from "./screens/Governance";
import { Login } from "./screens/Login";
import { Notes } from "./screens/Notes";
import { People } from "./screens/People";
import { Proposals } from "./screens/Proposals";
import { Search } from "./screens/Search";
import { Security } from "./screens/Security";
import { Settings } from "./screens/Settings";
import { SetPassword } from "./screens/SetPassword";
import { Tickets } from "./screens/Tickets";
import { Workspace } from "./screens/Workspace";
import { WorkspacePicker } from "./screens/WorkspacePicker";

/**
 * Per-screen crash containment.
 *
 * React unmounts the WHOLE tree when a render error reaches the root uncaught, so
 * before this every route was one throw away from replacing the entire SPA with a
 * blank page — sidebar, workspace switcher and navigation included. Wrapping each
 * route element individually (rather than putting one boundary around `<Routes>`)
 * is what keeps `<AppShell>` mounted: the child routes render into its `<Outlet>`,
 * so a boundary inside the outlet fails the screen and leaves the chrome alive and
 * clickable. The user can navigate away from a broken screen instead of reloading.
 *
 * `resetKeys={[pathname]}` matters as much as the boundary itself. An
 * `ErrorBoundary` holds its error state for its own lifetime, and these
 * boundaries outlive a route change — so without the reset key, navigating away
 * from a crashed screen and back would land in the SAME failed boundary and show
 * the fallback again forever.
 *
 * The fallback is the lib's `DefaultErrorFallback`: title, message, a retry wired
 * to `reset`, and a copyable `cor_*` correlation id that matches the one
 * `@norkron/web-errors` puts in the sink — so a client can quote an id an
 * operator can actually search for. The correlation id only arrives via
 * `onError`, which fires after the fallback first renders, hence the state.
 */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [correlationId, setCorrelationId] = useState<string | undefined>();

  const onError = useCallback((_e: Error, _i: unknown, id: string) => {
    setCorrelationId(id);
  }, []);

  return (
    <ErrorBoundary
      resetKeys={[pathname]}
      onError={onError}
      onReset={() => setCorrelationId(undefined)}
      fallback={(error, reset) => (
        <div className="page" data-testid="route-error">
          <DefaultErrorFallback
            error={error}
            reset={reset}
            correlationId={correlationId}
            title="This screen ran into a problem"
          />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Route map (ia-and-flows §1). Only /login and /set-password render without a
 * session; everything else is wrapped in <RequireAuth> — the shell serves no
 * data unauthenticated (§9 rule 1). The workspace plane lives under /w/:slug
 * inside the accent-themed AppShell.
 *
 * Every route element is wrapped in <RouteBoundary> (see above). The ORDER of the
 * two wrappers is deliberate: the boundary sits OUTSIDE <RequireAuth>, so a crash
 * in the auth check itself still renders a fallback rather than escaping to the
 * root and blanking the page.
 */
export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RouteBoundary>
            <Login />
          </RouteBoundary>
        }
      />
      <Route
        path="/set-password"
        element={
          <RouteBoundary>
            <SetPassword />
          </RouteBoundary>
        }
      />

      <Route
        path="/"
        element={
          <RouteBoundary>
            <RequireAuth>
              <WorkspacePicker />
            </RequireAuth>
          </RouteBoundary>
        }
      />

      <Route
        path="/settings/security"
        element={
          <RouteBoundary>
            <RequireAuth>
              <Security />
            </RequireAuth>
          </RouteBoundary>
        }
      />

      <Route
        path="/w/:slug"
        element={
          <RouteBoundary>
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          </RouteBoundary>
        }
      >
        {/* Article-unit pivot §5: the workspace lands ARTICLE-FIRST. The legacy
            document library stays reachable at /w/:slug/library (governance +
            legacy docs still live there). */}
        <Route
          index
          element={
            <RouteBoundary>
              <Articles />
            </RouteBoundary>
          }
        />
        <Route
          path="library"
          element={
            <RouteBoundary>
              <Workspace />
            </RouteBoundary>
          }
        />
        <Route
          path="search"
          element={
            <RouteBoundary>
              <Search />
            </RouteBoundary>
          }
        />
        <Route
          path="doc/:id"
          element={
            <RouteBoundary>
              <DocumentViewer />
            </RouteBoundary>
          }
        />
        <Route
          path="article/:unitId"
          element={
            <RouteBoundary>
              <ArticleWorkbench />
            </RouteBoundary>
          }
        />
        <Route
          path="people"
          element={
            <RouteBoundary>
              <People />
            </RouteBoundary>
          }
        />
        <Route
          path="admin"
          element={
            <RouteBoundary>
              <Admin />
            </RouteBoundary>
          }
        />
        <Route
          path="settings"
          element={
            <RouteBoundary>
              <Settings />
            </RouteBoundary>
          }
        />
        <Route
          path="tickets"
          element={
            <RouteBoundary>
              <Tickets />
            </RouteBoundary>
          }
        />
        <Route
          path="proposals"
          element={
            <RouteBoundary>
              <Proposals />
            </RouteBoundary>
          }
        />
        <Route
          path="notes"
          element={
            <RouteBoundary>
              <Notes />
            </RouteBoundary>
          }
        />
        <Route
          path="governance"
          element={
            <RouteBoundary>
              <Governance />
            </RouteBoundary>
          }
        />
        <Route
          path="activity"
          element={
            <RouteBoundary>
              <Activity />
            </RouteBoundary>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
